import * as React from "react";
import { createContext, Suspense, useContext, useEffect, useState, useMemo } from "react";
import { useLiveQuery } from "dexie-react-hooks";
import { BrowserRouter, Outlet, Route, Routes, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { SystemUpdateAlt } from "./icons";
import { Alert, AlertDescription, AlertTitle } from "@thuthi/ui/components/alert";
import { Button } from "@thuthi/ui/components/button";
import { Toaster } from "@thuthi/ui/components/sonner";
import { LoadingOverlay } from "@thuthi/ui/components/loading-overlay";
import { AllSubscriptions, SingleSubscription } from "./Notifications";
import Navigation from "./Navigation";
import ActionBar from "./ActionBar";
import Preferences from "./Preferences";
import subscriptionManager from "../app/SubscriptionManager";
import { expandUrl, getKebabCaseLangStr, updateFavicon } from "../app/utils";
import ErrorBoundary from "./ErrorBoundary";
import routes from "./routes";
import useAppDarkMode from "./theme";
import {
  useAccountListener,
  useBackgroundProcesses,
  useConnectionListeners,
  useNativeBackButtonBridge,
  useNativeBridgeSync,
  useNativePullRefreshState,
  useVersionChangeListener,
} from "./hooks";
import PublishDialog from "./PublishDialog";
import Messaging from "./Messaging";
import Login from "./Login";
import Account from "./Account";
import initI18n from "../app/i18n"; // Translations!
import session from "../app/Session";
import nativeBridge from "../app/NativeBridge";
import { event as nativeEvent } from "../app/NativeBridgeContract.generated";
import type { Account as ApiAccount, NativeNotification } from "../app/types";

initI18n();

export interface AccountContextType {
  account: ApiAccount | null;
  setAccount: React.Dispatch<React.SetStateAction<ApiAccount | null>>;
}

// 런타임 기본값은 원본과 동일하게 null 이다. 컨텍스트는 항상 App 의 Provider 아래에서만
// 소비되므로, 소비측 편의를 위해 타입만 non-null 로 단언한다.
export const AccountContext = createContext<AccountContextType>(null as unknown as AccountContextType);

const App = () => {
  const { i18n } = useTranslation();
  const [account, setAccount] = useState<ApiAccount | null>(null);
  const accountMemo = useMemo(() => ({ account, setAccount }), [account, setAccount]);
  useAppDarkMode();

  useEffect(() => {
    document.documentElement.setAttribute("lang", getKebabCaseLangStr(i18n.language));
  }, [i18n.language]);

  useEffect(() => {
    if (!nativeBridge.isNativeApp()) {
      return undefined;
    }
    document.documentElement.classList.add("ntfy-android-native");
    nativeBridge.log("React app mounted in Android wrapper");
    const requestNotificationExport = () => nativeBridge.exportNotifications();
    const unsubscribe = nativeBridge.onEvent(async (event) => {
      console.log("[NativeBridge] Event", event);
      if (event?.type === nativeEvent.nativeReady || event?.type === nativeEvent.foreground) {
        requestNotificationExport();
      } else if (event?.type === nativeEvent.notificationsExported) {
        await subscriptionManager.importNativeNotifications((event.payload?.notifications as NativeNotification[] | undefined) || []);
      } else if (event?.type === nativeEvent.subscriptionSynced && event.payload?.baseUrl && event.payload?.topic) {
        await subscriptionManager.add(event.payload.baseUrl as string, event.payload.topic as string, {
          displayName: (event.payload.displayName as string | undefined) || undefined,
          mutedUntil: (event.payload.mutedUntil as number | undefined) || 0,
        });
      }
    });
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        requestNotificationExport();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    requestNotificationExport();
    return () => {
      unsubscribe();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    // 이 배포는 deny-all(익명 접근 불가)이므로 로그인 없는 웹은 의미가 없다 —
    // require_login 설정과 무관하게 항상 /login(중앙 세션 브리지)으로 보낸다.
    if (!session.exists() && window.location.pathname !== routes.login) {
      window.location.href = routes.login;
    }
  }, []);

  return (
    <Suspense fallback={<LoadingOverlay />}>
      <BrowserRouter>
        <AccountContext.Provider value={accountMemo}>
          <ErrorBoundary>
            <Routes>
              <Route path={routes.login} element={<Login />} />
              <Route element={<Layout />}>
                <Route path={routes.app} element={<AllSubscriptions />} />
                <Route path={routes.account} element={<Account />} />
                <Route path={routes.settings} element={<Preferences />} />
                <Route path={routes.subscription} element={<SingleSubscription />} />
                <Route path={routes.subscriptionExternal} element={<SingleSubscription />} />
              </Route>
            </Routes>
          </ErrorBoundary>
          <Toaster position="bottom-right" />
        </AccountContext.Provider>
      </BrowserRouter>
    </Suspense>
  );
};

const updateTitle = (newNotificationsCount: number) => {
  document.title = newNotificationsCount > 0 ? `(${newNotificationsCount}) ntfy` : "ntfy";
  window.navigator.setAppBadge?.(newNotificationsCount);
  updateFavicon(newNotificationsCount);
};

const Layout = () => {
  const params = useParams();
  const { setAccount } = useContext(AccountContext);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [sendDialogOpenMode, setSendDialogOpenMode] = useState("");
  const [versionUpdateAvailable, setVersionUpdateAvailable] = useState(false);
  const handleMobileDrawerToggle = () => setMobileDrawerOpen((open) => !open);
  const handleMobileDrawerClose = () => setMobileDrawerOpen(false);
  const handleVersionUpdateAvailable = React.useCallback(() => setVersionUpdateAvailable(true), []);
  const subscriptions = useLiveQuery(() => subscriptionManager.all());
  const subscriptionsWithoutInternal = subscriptions?.filter((s) => !s.internal);
  const newNotificationsCount = subscriptionsWithoutInternal?.reduce((prev, cur) => prev + cur.new, 0) || 0;
  const [selected] = (subscriptionsWithoutInternal || []).filter(
    (s) =>
      (params.baseUrl && expandUrl(params.baseUrl).includes(s.baseUrl) && params.topic === s.topic) ||
      (config.base_url === s.baseUrl && params.topic === s.topic)
  );

  useConnectionListeners(subscriptions);
  useAccountListener(setAccount);
  useBackgroundProcesses();
  useNativeBridgeSync(subscriptions);
  useNativeBackButtonBridge();
  useNativePullRefreshState();
  useVersionChangeListener(handleVersionUpdateAvailable);
  useEffect(() => updateTitle(newNotificationsCount), [newNotificationsCount]);

  return (
    <div className="bg-background text-foreground flex h-dvh overflow-hidden overflow-x-clip">
      <Navigation
        subscriptions={subscriptionsWithoutInternal}
        selectedSubscription={selected}
        mobileDrawerOpen={mobileDrawerOpen}
        onMobileDrawerClose={handleMobileDrawerClose}
        onPublishMessageClick={() => setSendDialogOpenMode(PublishDialog.OPEN_MODE_DEFAULT)}
      />
      <div className="bg-background flex h-full min-w-0 grow flex-col">
        {versionUpdateAvailable && <VersionUpdateBanner />}
        <ActionBar selected={selected} onMobileDrawerToggle={handleMobileDrawerToggle} />
        <main id="main" className="flex w-full min-h-0 grow flex-col overflow-auto p-3 md:px-7 md:py-6">
          <Outlet
            context={{
              subscriptions: subscriptionsWithoutInternal,
              selected,
            }}
          />
        </main>
      </div>
      <Messaging selected={selected} dialogOpenMode={sendDialogOpenMode} onDialogOpenModeChange={setSendDialogOpenMode} />
    </div>
  );
};

const VersionUpdateBanner = () => {
  const { t } = useTranslation();
  const handleUpdate = () => {
    window.location.reload();
  };
  return (
    <Alert className="shrink-0 rounded-none border-x-0 border-t-0">
      {/* Alert 는 [아이콘 열 | 본문 열] 그리드다. 커스텀 자식은 col-start-2 가 없으면
          폭 0 인 아이콘 열에 들어가 글자가 한 줄에 한 자씩 세로로 깨진다. */}
      <div className="col-start-2 flex min-w-0 flex-col justify-between gap-3 sm:flex-row sm:items-center">
        <div className="min-w-0">
          <AlertTitle>{t("version_update_available_title")}</AlertTitle>
          <AlertDescription>{t("version_update_available_description")}</AlertDescription>
        </div>
        <Button variant="outline" size="sm" onClick={handleUpdate} className="shrink-0 self-start sm:self-center">
          <SystemUpdateAlt /> {t("version_update_available_button")}
        </Button>
      </div>
    </Alert>
  );
};

export default App;
